var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed, observable, reaction, when } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { FormModel } from "./FormModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { UserType } from "healthex-common/user.dtos";
export var CFR_DEFAULT;
(function (CFR_DEFAULT) {
    CFR_DEFAULT["REQUIRED"] = "With 21 CFR Part 11 compliance";
    CFR_DEFAULT["NOT_REQUIRED"] = "Without 21 CFR Part 11 compliance";
})(CFR_DEFAULT || (CFR_DEFAULT = {}));
export class AdminResearchSettingsViewModel {
    constructor(container) {
        this.editingCFR = false;
        this.cfrFormModel = new FormModel({
            require21CFRPart11ForInformedConsents: false,
            defaultRequire21CFRPart11ForInformedConsents: CFR_DEFAULT.NOT_REQUIRED,
        }, []);
        this.saveConfirmationDialog = new SimpleDialogModel();
        this.adminUIManager = container.adminUIManager;
        this.organizationsManager = container.organizationsManager;
        this.authManager = container.authManager;
        reaction(() => this.adminUIManager.sectionToExpand, async () => {
            await this.expandSectionIfNecessary();
        }, { fireImmediately: true });
    }
    get organization() {
        if (this.adminUIManager.organization === NOT_LOADED)
            return NOT_LOADED;
        if (this.adminUIManager.organization === null)
            return null;
        if (this.adminUIManager.organization.type !== UserType.HEALTHCARE_ORGANIZATION &&
            this.adminUIManager.organization.type !== UserType.EXTERNAL_RESEARCHER) {
            return null;
        }
        return this.adminUIManager.organization;
    }
    get require21CFRPart11ForInformedConsentsEnabled() {
        return this.cfrFormModel.input.require21CFRPart11ForInformedConsents;
    }
    get cfrText() {
        const isDataUser = this.authManager.isDataUser;
        return {
            title: isDataUser ? "21 CFR Part 11 required?" : "21 CFR Part 11 compliance for informed consents",
            text: isDataUser
                ? "Make 21 CFR Part 11 compliance required for all research enrollment projects led by your organization."
                : "Make 21 CFR Part 11 compliance required for all informed consents for all research led by your organization.",
            cfrText: isDataUser
                ? "Code of Federal Regulations Title 21 Part 11 compliance ensures that all research enrollment projects meet regulatory standards established by the Food and Drug Administration (FDA)."
                : "Code of Federal Regulations Title 21 Part 11 compliance ensures that all informed consents meet regulatory standards established by the Food and Drug Administration (FDA).",
            cfrText2: isDataUser
                ? "If required, HealthEx will ensure all projects led by your organization will meet 21 CFR Part 11 compliance requirements. If it’s not required, the organization will have the option to enable compliance on a per-project basis."
                : "If required, HealthEx will ensure all research led by your organization will meet 21 CFR Part 11 compliance requirements. If it’s not required, the organization will have the option to enable compliance on a per-study basis.",
            checkboxLabel: isDataUser
                ? "Require 21 CFR Part 11 compliance for all research enrollment projects"
                : "Require 21 CFR Part 11 compliance for all informed consents",
            cfrDefaultText: isDataUser
                ? "If 21 CFR Part 11 compliance is not required, each project led by your organization can choose if they want to be compliant. Please select a default for the compliance."
                : "If 21 CFR Part 11 compliance is not required, each study led by your organization can choose if they want to be compliant. Please select a default for the compliance.",
        };
    }
    async expandSectionIfNecessary() {
        if (this.adminUIManager.sectionToExpand === "CFR") {
            await this.toggleEditingCFR(true);
        }
    }
    async toggleEditingCFR(force) {
        const newValue = force !== null && force !== void 0 ? force : !this.editingCFR;
        if (!newValue && this.hasCFRChanged) {
            const confirm = await this.showConfirmSaveDialog();
            if (!confirm)
                return;
        }
        this.editingCFR = newValue;
        if (this.editingCFR) {
            await this.loadCFR();
        }
    }
    async loadCFR() {
        var _a, _b, _c;
        await when(() => this.organization !== NOT_LOADED);
        if (this.organization === NOT_LOADED)
            return;
        this.cfrFormModel.updateInput({
            require21CFRPart11ForInformedConsents: (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.require21CFRPart11ForInformedConsents) !== null && _b !== void 0 ? _b : false,
            defaultRequire21CFRPart11ForInformedConsents: ((_c = this.organization) === null || _c === void 0 ? void 0 : _c.defaultRequire21CFRPart11ForInformedConsents)
                ? CFR_DEFAULT.REQUIRED
                : CFR_DEFAULT.NOT_REQUIRED,
        });
    }
    async saveCFR() {
        if (this.organization === NOT_LOADED || !this.organization)
            return;
        const { require21CFRPart11ForInformedConsents, defaultRequire21CFRPart11ForInformedConsents } = this.cfrFormModel.input;
        const updatedOrganizationDetailsInput = {
            type: this.organization.type,
            require21CFRPart11ForInformedConsents,
            defaultRequire21CFRPart11ForInformedConsents: defaultRequire21CFRPart11ForInformedConsents === CFR_DEFAULT.REQUIRED,
        };
        await this.organizationsManager.updateOrganizationDetails(this.organization.id, updatedOrganizationDetailsInput);
        await this.organizationsManager.waitForReloadOrganizations();
        await this.toggleEditingCFR(false);
    }
    get hasCFRChanged() {
        var _a, _b;
        if (this.organization === NOT_LOADED || !this.organization)
            return false;
        return (this.cfrFormModel.input.require21CFRPart11ForInformedConsents !== ((_a = this.organization) === null || _a === void 0 ? void 0 : _a.require21CFRPart11ForInformedConsents) ||
            (this.cfrFormModel.input.defaultRequire21CFRPart11ForInformedConsents === CFR_DEFAULT.REQUIRED) !==
                ((_b = this.organization) === null || _b === void 0 ? void 0 : _b.defaultRequire21CFRPart11ForInformedConsents));
    }
    async showConfirmSaveDialog() {
        this.saveConfirmationDialog.show();
        return await this.saveConfirmationDialog.showAndWait();
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminResearchSettingsViewModel.prototype, "editingCFR", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminResearchSettingsViewModel.prototype, "organization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminResearchSettingsViewModel.prototype, "require21CFRPart11ForInformedConsentsEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminResearchSettingsViewModel.prototype, "cfrText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminResearchSettingsViewModel.prototype, "hasCFRChanged", null);
